// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FormControl, TextField, InputLabel, Select, MenuItem, Button } from '@material-ui/core';
import MaterialTable from 'material-table';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';

// Internal Dependencies
import useDebounce from '../global/use-debounce';
import { updateModalContent, toggleModal } from '../../actions/modal';
import apiService from '../../services.js/apiService';
import { IsEmpty } from '../../common/checks';
import SortArrow from '../common/SortArrow';
import { formatDateToDayMonthNameYear, showDateInSGTime, Trim } from '../../common/util';

const themeTable = createMuiTheme({
  overrides: {
    MuiTableRow: {
      root: {
        '&:hover, &:nth-child(odd):hover': {
          transition:      '0.3s ease',
          backgroundColor: '#ffecb3',
        },
        '&:nth-child(odd)': {
          backgroundColor: '#eeeeee',
        },
      },
    },
  },
});

const ReviewsTable = (props) => {
  const tableRef = useRef(null);
  const columns = [
    {
      title:   'Image', field:   'image', width:   '5%',
      sorting: false,
      render:  (review) => (
        <div
          style={{
            background:   `url(${review.image}) center center / cover no-repeat rgb(255, 255, 255)`,
            borderRadius: '5px',
            minWidth:     '50px',
            maxWidth:     '50px',
            height:       '50px',
            border:       '1px solid #CCC',
          }}
        />
      ),
    },
    {
      title:     'Id',
      field:     'id',
      filtering: false,
      width:     '5%',
      sorting:   false,
      render:    (review) => <a href={`page/${review.pageId}/review/${review.id}`} target="_blank" rel="noreferrer">{review.id}</a>,
    },
    { title: 'Reviewer page id', field: 'pageId', filtering: false, width: '5%', sorting: false },
    { title: 'Reviewer page name', field: 'pageName', filtering: false, width: '5%', render: (review) => review.pgName || '--', sorting: false },
    { title: 'Title', field: 'title', filtering: false, render: (review) => (!IsEmpty(review.title) ? review.title.replace(/(.{100})..+/, '$1…') : ''), sorting: false },
    {
      title:     'Review Date',
      field:     'reviewDate',
      width:     '15%',
      cellStyle: {
        minWidth: 120,
      },
      sorting: false,
      render:  (review) => (review.reviewDate !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(review.reviewDate), true)}` : '-'),
    },
    { title: 'Status', field: 'status', filtering: false, width: '5%', sorting: false },
    { title: 'Searchable Status', field: 'searchableStatus', filtering: false, width: '5%', sorting: false },
    {
      title:     'Updated At',
      field:     'updatedAt',
      width:     '15%',
      cellStyle: {
        minWidth: 120,
      },
      sorting: true,
      render:  (review) => (review.updatedAt !== null ? `${formatDateToDayMonthNameYear(showDateInSGTime(review.updatedAt), true)}` : '-'),
    },
    {
      title:   'Last Updated By',
      field:   'lastUpdatedBy',
      width:   '15%',
      sorting: false,
      render:  (review) => (!IsEmpty(review.lastUpdatedBy) ? review.lastUpdatedBy : '-'),
    },
  ];

  const initializeFocusValue = {
    searchFocus: false,
  };

  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const { idFocus, postalCodeFocus, placeNameFocus } = autoFocus;

  const [reviewId, setReviewId] = useState(null);
  const debounceId = useDebounce(reviewId, 500);
  const [status, setStatus] = useState('');
  const [searchableStatus, setSearchableStatus] = useState('');

  const [pageName, setPageName] = useState('');
  const [currentOrder, setCurrentOrder] = useState('');

  const [pageNamesOption, setPageNamesOption] = useState([]);
  const [currentSelectedPageName, setCurrentSelectedPageName] = useState('');

  const [postalCode, setPostalCode] = useState('');
  const debouncePostalCode = useDebounce(postalCode, 500);

  const [areaList, setAreaList] = useState([]);
  const [district, setDistrict] = useState(null);

  const [placeName, setPlaceName] = useState('');
  const debouncePlaceName = useDebounce(placeName, 500);

  const [pageSize, setPageSize] = useState(20);

  areaList.sort((a, b) => {
    const areaA = a.name.toUpperCase();
    const areaB = b.name.toUpperCase();
    if (areaA < areaB) {
      return -1;
    }
    if (areaA > areaB) {
      return 1;
    }
    return 0;
  });

  useEffect(() => {
    tableRef.current.onQueryChange();
  }, [debounceId, status, searchableStatus, pageName, debouncePostalCode, debouncePlaceName, district]);

  const getReviewPages = async () => {
    try {
      const data = await apiService.getReviewPages();
      if (!IsEmpty(data.rows)) {
        const pageNames = _.map(data.rows, (pg) => pg.name);
        setPageNamesOption(pageNames);
      }
    } catch (error) {
      return toast.error(error.message);
    }
  };

  const handleChange = (e) => {
    if (e.target.name === 'filter-id') {
      setReviewId(e.target.value);
      setAutoFocus({ idFocus: true });
    } else if (e.target.name === 'filter-status') {
      setStatus(e.target.value);
    } else if (e.target.name === 'filter-searchable-status') {
      setSearchableStatus(e.target.value);
    } else if (e.target.name === 'filter-district') {
      setDistrict(e.target.value);
    } else if (e.target.name === 'filter-postal-code') {
      setAutoFocus({ postalCodeFocus: true });
      setPostalCode(e.target.value);
    } else if (e.target.name === 'filter-place-name') {
      setAutoFocus({ placeNameFocus: true });
      setPlaceName(e.target.value);
    }
  };

  const handleDelete = (id) => {
    props.updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this post will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          const result = await apiService.deleteReview(id);
          if (result) {
            toast.success('Review has been deleted');
            tableRef.current.onQueryChange();
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    props.toggleModal();
  };

  const populateAreas = async () => {
    try {
      const areas = await apiService.getAreas();
      setAreaList(areas);
    } catch (err) {
      return toast.error(err);
    }
  };

  const clearFilter = () => {
    if (tableRef.current) {
      setReviewId(null);
      setCurrentSelectedPageName('');
      setPageName('');
      setStatus('');
      setSearchableStatus('');
      setPostalCode('');
      setAreaList([]);
      setDistrict(null);
      setPlaceName('');

      // re-populate district/areas
      populateAreas();

      // refresh table
      tableRef.current.onQueryChange();
    }
  };

  useEffect(() => {
    populateAreas();
    getReviewPages();
  }, []);

  return (
    <div>
      <ThemeProvider theme={themeTable}>
        <MaterialTable
          tableRef={tableRef}
          className="material-table"
          title=""
          columns={columns}
          icons={{
            SortArrow: () => <SortArrow currentOrder={currentOrder} onSort={(order) => setCurrentOrder(order)} />,
          }}
          onChangeRowsPerPage={() => {
            setCurrentOrder('');
            setPageSize(pageSize);
          }}
          data={(query) => new Promise(async (resolve, reject) => {
            try {
              query.id = reviewId;
              query.status = status;
              query.searchableStatus = searchableStatus;
              query.pageName = pageName;
              query.postalCode = postalCode;
              query.placeName = Trim(placeName);
              query.area = district;

              query.pageNo = query.page;

              if (!IsEmpty(currentOrder)) {
                query.order = ['updatedAt', currentOrder];
              }
              
              const result = await apiService.getReviewsByParams(query);

              let { rows, totalCount } = result;
              let { page } = query;

              if (rows.length === 0) {
                page = 0;

                if (totalCount !== 0) {
                  const resultFirstPage = await apiService.getReviewsByParams({ ...query, page });
                  rows = resultFirstPage.rows;
                }
              }

              resolve({
                data: rows,
                page,
                totalCount,
              });
            } catch (err) {
              toast.error(err.message);
              resolve({
                data:       [],
                page:       0,
                totalCount: 0,
              });
            }
          })}
          actions={[
            {
              icon:    'edit',
              tooltip: 'Edit Review',
              onClick: (event, review) => {
                window.open(`/admin/page/${review.pageId}/review/${review.id}`, '_blank');
              },
            },
            {
              icon:    'delete',
              tooltip: 'Delete Review',
              onClick: (event, review) => {
              handleDelete(review.id);
              },
            },
          ]}
          options={{
            pageSize,
            actionsColumnIndex:  -1,
            emptyRowsWhenPaging: false,
            search:              false,
          }}
          components={{
            Toolbar: () => (
              <div className="my-4 py-4">
                <div className="row mx-1">
                  <div className="col-md-1">
                    <FormControl fullWidth>
                      <TextField
                        label="Search by id"
                        type="number"
                        name="filter-id"
                        value={reviewId || ''}
                        onChange={(e) => handleChange(e)}
                        autoFocus={idFocus}
                      />
                    </FormControl>
                  </div>

                  <div className="col-md-2">
                    <FormControl fullWidth>
                      <Autocomplete
                        disableCloseOnSelect
                        filterSelectedOptions
                        freeSolo
                        options={pageNamesOption}
                        getOptionLabel={(option) => option}
                        noOptionsText="No match suggestion."
                        value={currentSelectedPageName}
                        onChange={(e, values) => {
                          setPageName(values);
                          setCurrentSelectedPageName(values);
                        }}
                        renderInput={(params) => <TextField {...params} label="Reviewer Page" />}
                      />
                    </FormControl>
                  </div>

                  <div className="col-md-2">
                    <FormControl fullWidth>
                      <TextField
                        label="Location/Place Name"
                        type="text"
                        name="filter-place-name"
                        value={placeName || ''}
                        onChange={(e) => handleChange(e)}
                        autoFocus={placeNameFocus}
                      />
                    </FormControl>
                  </div>

                  <div className="col-md-2">
                    <FormControl fullWidth>
                      <InputLabel id="filter-district-label">District</InputLabel>
                      <Select
                        labelId="filter-district-label"
                        value={district}
                        name="filter-district"
                        onChange={(e) => handleChange(e)}
                      >
                        <MenuItem value="">--</MenuItem>

                        { !IsEmpty(areaList) && _.map(areaList, (a) => <MenuItem value={a.name}>{ a.name }</MenuItem>) }

                      </Select>
                    </FormControl>
                  </div>

                  <div className="col-md-1">
                    <FormControl fullWidth>
                      <TextField
                        label="Postal code"
                        type="number"
                        name="filter-postal-code"
                        value={postalCode || ''}
                        onChange={(e) => handleChange(e)}
                        autoFocus={postalCodeFocus}
                      />
                    </FormControl>
                  </div>

                  <div className="col-md-1">
                    <FormControl fullWidth>
                      <InputLabel id="status-filter-label">Status</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        value={status}
                        name="filter-status"
                        onChange={(e) => handleChange(e)}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Draft">Draft</MenuItem>
                        <MenuItem value="Live">Live</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </div>

                  <div className="col-md-2">
                    <FormControl fullWidth>
                      <InputLabel id="searchableStatus-filter-label">Searchable Status</InputLabel>
                      <Select
                        labelId="searchableStatus-filter-label"
                        value={searchableStatus}
                        name="filter-searchable-status"
                        onChange={(e) => handleChange(e)}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Live">Live</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Expired">Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </div>

                  <div className="col-md-1">
                    <FormControl fullWidth>
                      <Button variant="contained" color="secondary" onClick={() => clearFilter()}>Clear filters</Button>
                    </FormControl>
                  </div>
                </div>
              </div>
            ),
          }}
        />
      </ThemeProvider>
    </div>
  );
};

ReviewsTable.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

const mapActionToProps = {
  updateModalContent,
  toggleModal,
};

export default connect(null, mapActionToProps)(ReviewsTable);
